export const setPasswordSchema = {
  body: {
    type: "object",
    properties: {
      password: { type: "string" },
      token: { type: "string", minLength: 1 },
    },
    required: ["password", "token"],
    additionalProperties: false,
  },
};

// A claim code sent along with a sign-in started from the claim screen. The
// claim runs right after the sign-in (claimedVia "link").
const signInClaimCodeSchema = { type: "string", minLength: 1, maxLength: 32 };

export const loginSchema = {
  body: {
    type: "object",
    properties: {
      email: { type: "string" },
      password: { type: "string" },
      claimCode: signInClaimCodeSchema,
    },
    required: ["email", "password"],
  },
};

export const inviteSchema = {
  params: {
    type: "object",
    properties: {
      userPublicId: { type: "string" },
    },
    required: ["userPublicId"],
  },
};

export const getTokenSchema = {
  params: {
    type: "object",
    properties: {
      token: { type: "string" },
    },
    required: ["token"],
  },
};

export const emailRequiredSchema = {
  body: {
    type: "object",
    properties: {
      email: { type: "string" },
      // The app's language, for the email's words.
      language: { type: "string", pattern: "^[a-z]{2}$" },
    },
    required: ["email"],
  },
};

// Email sign-in: a 6-digit code sent to the address, typed into the app.
export const emailSignInStartSchema = {
  body: {
    type: "object",
    properties: {
      email: { type: "string", format: "email", maxLength: 255 },
      // The app's language, for the email's words.
      language: { type: "string", pattern: "^[a-z]{2}$" },
    },
    required: ["email"],
    additionalProperties: false,
  },
};

export const emailSignInVerifySchema = {
  body: {
    type: "object",
    properties: {
      email: { type: "string", format: "email", maxLength: 255 },
      code: { type: "string", pattern: "^[0-9]{6}$" },
      claimCode: signInClaimCodeSchema,
    },
    required: ["email", "code"],
    additionalProperties: false,
  },
};

export const web_emailSignInSchema = {
  type: "object",
  properties: {
    email: { type: "string", minLength: 1, format: "email" },
    password: { type: ["string", "null"], maxLength: 0 },
  },
  required: ["email"],
  additionalProperties: false,
};

export const web_regularLoginSchema = {
  type: "object",
  properties: {
    email: { type: "string", minLength: 1, format: "email" },
    password: { type: "string", minLength: 6 },
  },
  required: ["email", "password"],
  additionalProperties: false,
};

export const web_confirmPasswordSchema = {
  type: "object",
  properties: {
    password: {
      type: "string",
      minLength: 3,
      pattern: "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d).{8,}$",
      // Require lowercase, uppercase, and digit
    },
    confirmPassword: {
      type: "string",
      const: { $data: "1/password" },
      // Ensure confirmPassword matches password
    },
  },
  required: ["password", "confirmPassword"],
  errorMessage: {
    properties: {
      password:
        "Password must contain at least 8 characters, a lowercase, an uppercase, and a digit",
      confirmPassword: "Passwords do not match",
    },
  },
};

export const mobile_passwordLoginSchema = {
  type: "object",
  properties: {
    email: { type: "string", minLength: 1 },
    password: { type: ["string"], minLength: 6 },
  },
  required: ["email", "password"],
  additionalProperties: false,
};

export const mobile_emailSchema = {
  type: "object",
  properties: {
    email: { type: "string", minLength: 1 },
    password: { type: ["string", "null"], maxLength: 0 },
  },
  required: ["email"],
  additionalProperties: false,
};

export const mobile_forgotPasswordSchema = {
  type: "object",
  properties: {
    email: { type: "string", minLength: 1 },
  },
  required: ["email"],
  additionalProperties: false,
};

// AUTH SESSIONS

export const refreshSessionSchema = {
  body: {
    type: "object",
    properties: {
      refreshToken: { type: "string", minLength: 1, maxLength: 200 },
    },
    required: ["refreshToken"],
    additionalProperties: false,
  },
};

// Logout normally names the session by its access token. A client whose
// access token already expired sends its refresh token instead, so it can
// revoke without refreshing first.
export const logoutSchema = {
  body: {
    type: ["object", "null"],
    properties: {
      refreshToken: { type: "string", minLength: 1, maxLength: 200 },
    },
    additionalProperties: false,
  },
};

const personNameSchema = {
  type: "string",
  minLength: 1,
  maxLength: 255,
  pattern: "\\S",
};

export const anonymousSignInSchema = {
  body: {
    type: "object",
    properties: {
      firstName: personNameSchema,
      lastName: personNameSchema,
    },
    required: ["firstName", "lastName"],
    additionalProperties: false,
  },
};

// Linking takes the same proof as the matching sign-in. `moveTickets` is the
// confirmed repeat after a 409 IDENTITY_IN_USE.
export const linkGoogleSchema = {
  body: {
    type: "object",
    properties: {
      token: {
        anyOf: [
          { type: "string", minLength: 1 },
          {
            type: "object",
            properties: { credential: { type: "string", minLength: 1 } },
            required: ["credential"],
          },
        ],
      },
      moveTickets: { type: "boolean", default: false },
    },
    required: ["token"],
  },
};

export const linkAppleSchema = {
  body: {
    type: "object",
    properties: {
      authorization: {
        type: "object",
        properties: { id_token: { type: "string", minLength: 1 } },
        required: ["id_token"],
      },
      isMobile: { type: "boolean" },
      nonce: { type: "string" },
      moveTickets: { type: "boolean", default: false },
    },
    required: ["authorization"],
  },
};

export const linkEmailStartSchema = {
  body: {
    type: "object",
    properties: {
      email: { type: "string", format: "email", maxLength: 255 },
      // The app's language, for the email's words.
      language: { type: "string", pattern: "^[a-z]{2}$" },
    },
    required: ["email"],
    additionalProperties: false,
  },
};

export const linkEmailVerifySchema = {
  body: {
    type: "object",
    properties: {
      email: { type: "string", format: "email", maxLength: 255 },
      code: { type: "string", pattern: "^[0-9]{6}$" },
      moveTickets: { type: "boolean", default: false },
    },
    required: ["email", "code"],
    additionalProperties: false,
  },
};
